import { useEffect, useMemo, useRef } from 'react';
import { editorBaseClasses } from '@root/helpers/editor';
import Placeholder from '@tiptap/extension-placeholder';
import type { Editor } from '@tiptap/react';
import { useEditor as tiptapUseEditorHook } from '@tiptap/react';
import clsx from 'clsx';
import { debounce } from 'lodash-es';

import { prepareBaseEditorExtensions } from '../extensions/base-extensions';
import type { UseEditorHookProps } from '../types';
import { createPasteEditorProps } from '../utils/paste-editor-props';

import { useImageExtension } from './use-image-extension';
import { useMentionExtension } from './use-mention-extension';

export const useEditor = ({
  documentType,
  name,
  editorContent = {},
  enableImageInsert = false,
  enablePageBreak = true,
  onUpdate,
  editable,
  unformattedPaste = false,
  placeholderText = 'Block content',
  getDatapointInserterRenderer,
  datapointInserterItems,
}: UseEditorHookProps): Editor | null => {
  const MentionExtension = useMentionExtension({ getDatapointInserterRenderer, datapointInserterItems });
  const ImageExtension = useImageExtension({ enableImageInsert });
  const extensions = useMemo(
    () =>
      prepareBaseEditorExtensions(
        [
          Placeholder.configure({
            placeholder: placeholderText,
          }),
          MentionExtension,
          ImageExtension,
        ],
        { enablePageBreak },
      ),
    // eslint-disable-next-line -- Update extensions only on editor mount
    [name],
  );

  const userContent = editorContent[name] ?? '';
  const unformattedPasteRef = useRef(unformattedPaste);

  useEffect(() => {
    unformattedPasteRef.current = unformattedPaste;
  }, [unformattedPaste]);

  const handleEditorUpdate = debounce(async (content) => {
    await onUpdate(content);
    // previous 200ms causes fail in tests (content is not updated in state)
  }, 10);

  useEffect(
    () => () => handleEditorUpdate.cancel(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cancel the mount-time debounce on unmount
    [],
  );

  const editor = tiptapUseEditorHook(
    {
      enablePasteRules: false, // disable Markdown when pasting
      enableInputRules: false, // disable Markdown when typing
      autofocus: true,
      onUpdate({ editor }: any) {
        handleEditorUpdate(editor.getHTML());
      },
      editorProps: {
        attributes: {
          class: clsx(editorBaseClasses, documentType.toLowerCase()),
        },
        ...createPasteEditorProps(unformattedPasteRef),
      },
      extensions,
    },
    [],
  );

  // https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1548336990
  useEffect(() => {
    if (editor) {
      setTimeout(() => {
        (editor as any).commands.setContent(userContent);
      }, 1);
    }
    // eslint-disable-next-line -- Update content only on editor mount
  }, [editor]);

  if (editor) {
    (editor as any).setOptions({ editable });
  }

  return editor;
};
